import React, { useState, useEffect } from "react";
import { C, font, text } from "../theme";
import { PasswordInput } from "../components/ui";
import TwoFactorVerify from "./TwoFactorVerify";
import TwoFactorEnroll from "./TwoFactorEnroll";

// A rate-limited request names its real wait (UX-80): the limiter window is up
// to 15 minutes and the server sends Retry-After — "a few minutes" made users poll.
export function retryAfterText(res) {
  const header = res.headers && res.headers.get && res.headers.get("Retry-After");
  const secs = Number(header);
  if (header && Number.isFinite(secs) && secs > 0) {
    const mins = Math.max(1, Math.ceil(secs / 60));
    return `Too many attempts from your network (the limit is shared with colleagues on the same connection). Wait about ${mins} minute${mins === 1 ? "" : "s"}, then try again.`;
  }
  return "Too many attempts from your network (the limit is shared with colleagues on the same connection). Wait up to 15 minutes, then try again.";
}

export default function LoginPage({ onLoggedIn, signedOut = false }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [step, setStep] = useState("login"); // "login" | "verify" | "enroll"
  const params = new URLSearchParams(window.location.search);
  const urlError = params.get("error");

  useEffect(() => { document.title = "Sign in — SGNIC P2P"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    let res;
    try {
      res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
    } catch {
      setErr("Can't reach the server. Check your connection and try again.");
      return;
    }
    if (!res.ok) {
      if (res.status === 429) setErr(retryAfterText(res));
      else if (res.status >= 500) setErr("Something went wrong on our end. Please try again shortly.");
      else setErr("Invalid email or password");
      return;
    }
    const body = await res.json().catch(() => ({}));
    if (body.twoFactorRequired) { setStep("verify"); return; }
    if (body.enrollTwoFactor) { setStep("enroll"); return; }
    onLoggedIn(); // mustChangePassword or plain success — App.js handles the next gate
  };

  // "Back to sign in" from the 2FA challenge (UX-73) lands here with the form intact.
  if (step === "verify") return <TwoFactorVerify onVerified={onLoggedIn} onBack={() => { setStep("login"); setErr(""); }} />;
  if (step === "enroll") return <TwoFactorEnroll onEnrolled={onLoggedIn} />;

  return (
    <div style={{ fontFamily: font, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.gray50 }}>
      <form onSubmit={submit} style={{ background: C.white, padding: 32, borderRadius: 10, border: `1px solid ${C.gray200}`, width: 360, display: "flex", flexDirection: "column", gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: text.h1, color: C.gray1100 }}>SGNIC P2P</h1>
        {signedOut && (
          <div role="status" style={{ background: C.warningBg, color: C.gray800, border: `1px solid ${C.warning}`, borderRadius: 6, padding: "8px 12px", fontSize: text.sm }}>
            You've been signed out — your session expired or was reset. Sign in again to continue.
          </div>
        )}
        {urlError === "not_invited" && <div role="alert" style={{ color: C.danger, fontSize: text.md }}>That account has not been invited.</div>}
        {err && <div role="alert" style={{ color: C.danger, fontSize: text.md }}>{err}</div>}
        <input type="email" aria-label="Email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required
          style={{ padding: "10px 12px", border: `1px solid ${C.gray300}`, borderRadius: 6 }} />
        <PasswordInput aria-label="Password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required />
        <button type="submit" style={{ padding: "10px 12px", background: C.gray1100, color: C.white, border: "none", borderRadius: 6, cursor: "pointer" }}>Sign in</button>
        <div style={{ textAlign: "center", color: C.gray500, fontSize: text.sm }}>or</div>
        <a href="/api/auth/entra/login" style={{ padding: "10px 12px", textAlign: "center", border: `1px solid ${C.gray300}`, borderRadius: 6, textDecoration: "none", color: C.gray1100 }}>Sign in with Microsoft</a>
      </form>
    </div>
  );
}
